import { useId } from "react";
import { useAnnounce } from "@/app/Announcer";
import { useI18n, useT } from "./I18nProvider";
import type { Locale } from "./locales";

/**
 * 표시 언어 선택 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * - **라우트·쿼리·필터를 보존합니다.** 바뀌는 것은 `lang` 하나뿐이고, 화면을 다시
 *   마운트하지 않으므로 스크롤·선택 상태가 유지됩니다.
 * - **포커스를 유지합니다.** 선택 뒤에도 포커스는 이 select에 그대로 있습니다 —
 *   언어를 바꿨다고 사용자를 다른 곳으로 옮기지 않습니다.
 * - 언어 이름은 **그 언어로** 씁니다(한국어 / English). 번역하면 자기 언어를
 *   못 읽는 사용자가 자기 언어를 찾지 못합니다.
 */
/** 언어 이름 메시지 ID. 템플릿으로 만들지 않습니다 — 게이트가 ID를 정적으로 셉니다. */
const LOCALE_LABEL_ID: Record<Locale, string> = { ko: "common.locale.ko", en: "common.locale.en" };

export function LocaleSelector() {
  const { locale, supported, setLocale } = useI18n();
  const { t } = useT("common");
  const announce = useAnnounce();
  const id = useId();

  return (
    <span className="locale-select">
      <label className="visually-hidden" htmlFor={id}>
        {t("common.locale.label")}
      </label>
      <select
        id={id}
        className="locale-select__control"
        value={locale}
        data-testid="locale-select"
        onChange={(event) => {
          const next = event.currentTarget.value as Locale;
          if (next === locale) return;
          setLocale(next);
          announce("locale", t("common.locale.changed", { locale: t(LOCALE_LABEL_ID[next]) }));
        }}
      >
        {supported.map((value) => (
          <option key={value} value={value}>
            {t(LOCALE_LABEL_ID[value])}
          </option>
        ))}
      </select>
    </span>
  );
}
